import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import CreateDownloadDrawer from './CreateDownloadDrawer';
import EditDeleteDownloadDrawer from './EditDeleteDownloadDrawer';
import { PlusIcon } from '@heroicons/react/20/solid';

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
  draft: 'bg-yellow-100 text-yellow-800',
};

const TABLE_CELL_CLASSES = 'px-4 py-3 sm:px-2 sm:py-2';

const getInitials = (text = '') => {
  return text
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const DownloadsTable = () => {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [editingDownload, setEditingDownload] = useState(null);

  const fetchDownloads = async () => {
    try {
      const res = await api.get('/api/downloads/all');
      setDownloads(res.data);
    } catch (err) {
      console.error('Error al cargar descargas:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDownloads();
  }, []);

  const openCreateDrawer = () => {
    setEditingDownload(null);
    setShowCreateDrawer(true);
  };

  const openEditDrawer = (download) => {
    setEditingDownload(download);
    setShowEditDrawer(true);
  };

  const handleCloseDrawers = () => {
    setShowCreateDrawer(false);
    setShowEditDrawer(false);
    setEditingDownload(null);
  };

  return (
    <div className="bg-[oklch(97%_0.014_254.604)] min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="bg-white shadow-md rounded-xl p-6">
          <div className="sm:flex sm:items-center sm:justify-between mb-6">
            <div>
              <h1 id="downloads-table-title" className="text-2xl font-semibold text-gray-900">Descargas</h1>
              <p className="mt-1 text-sm text-gray-600">
                Lista de archivos descargables disponibles para tus usuarios incluyendo nombre, slug y estado.
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <button
                onClick={openCreateDrawer}
                className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
                aria-label="Agregar nueva descarga"
              >
                <PlusIcon className="h-4 w-4 mr-1" /> Agregar
              </button>
            </div>
          </div>

          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-100 animate-pulse rounded"></div>
              ))}
            </div>
          ) : downloads.length === 0 ? (
            <div className="text-center py-10 text-gray-500">No hay descargas registradas.</div>
          ) : (
            <>
              <div className="overflow-x-auto lg:block hidden">
                <table
                  role="table"
                  aria-labelledby="downloads-table-title"
                  className="min-w-full divide-y divide-gray-200 text-sm"
                >
                  <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-600">
                    <tr>
                      <th className={TABLE_CELL_CLASSES}>Nombre</th>
                      <th className={TABLE_CELL_CLASSES}>Slug</th>
                      <th className={TABLE_CELL_CLASSES}>Código</th>
                      <th className={TABLE_CELL_CLASSES}>Categoría</th>
                      <th className={TABLE_CELL_CLASSES}>Estado</th>
                      <th className={TABLE_CELL_CLASSES}>Visible</th>
                      <th className={TABLE_CELL_CLASSES}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {downloads.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className={TABLE_CELL_CLASSES}>
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 flex items-center justify-center bg-gray-200 rounded-full text-xs font-bold text-gray-600">
                              {getInitials(item.name)}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{item.name}</p>
                              <p className="text-xs text-gray-500 font-mono">ID: {item.id}</p>
                            </div>
                          </div>
                        </td>
                        <td className={TABLE_CELL_CLASSES} style={{ wordBreak: 'break-word' }}>
                          <span className="text-blue-700">{item.slug}</span>
                        </td>
                        <td className={TABLE_CELL_CLASSES}>
                          <span className="font-mono text-gray-600">{item.downloader_code}</span>
                        </td>
                        <td className={TABLE_CELL_CLASSES}>
                          {item.category_name ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {item.category_name}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className={TABLE_CELL_CLASSES}>
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[item.status] || ''}`}>
                            {item.status}
                          </span>
                        </td>
                        <td className={TABLE_CELL_CLASSES}>
                          <span className="text-gray-600 text-xs capitalize">{item.is_visible}</span>
                        </td>
                        <td className={TABLE_CELL_CLASSES}>
                          <button
                            onClick={() => openEditDrawer(item)}
                            className="text-blue-700 hover:underline text-sm"
                            aria-label={`Editar ${item.name}`}
                          >
                            Editar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="lg:hidden space-y-4">
                {downloads.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white border border-gray-200 rounded-lg shadow-sm p-3 flex flex-col sm:flex-row items-start justify-between gap-2"
                    role="article"
                    aria-labelledby={`download-${item.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 sm:h-10 sm:w-10 flex items-center justify-center bg-gray-200 rounded-full text-xs font-bold text-gray-600">
                        {getInitials(item.name)}
                      </div>
                      <div className="space-y-1">
                        <h2 id={`download-${item.id}`} className="font-medium text-gray-900 text-sm">{item.name}</h2>
                        <p className="text-xs text-gray-500 font-mono">ID: {item.id}</p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:items-end space-y-1 w-full sm:w-auto">
                      <p className="text-blue-700 text-sm break-words">{item.slug}</p>
                      <p className="font-mono text-gray-600 text-sm">{item.downloader_code}</p>
                      <p>
                        {item.category_name ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                            {item.category_name}
                          </span>
                        ) : (
                          <span className="text-gray-600">-</span>
                        )}
                      </p>
                      <p className="text-gray-600 text-xs capitalize">{item.is_visible}</p>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[item.status] || ''}`}>
                        {item.status}
                      </span>
                      <button
                        onClick={() => openEditDrawer(item)}
                        className="text-blue-700 hover:underline text-sm mt-1"
                        aria-label={`Editar ${item.name}`}
                      >
                        Editar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <CreateDownloadDrawer isOpen={showCreateDrawer} onClose={handleCloseDrawers} onCreated={fetchDownloads} />
      <EditDeleteDownloadDrawer
        isOpen={showEditDrawer}
        onClose={handleCloseDrawers}
        download={editingDownload}
        onUpdated={fetchDownloads}
        onDeleted={fetchDownloads}
      />
    </div>
  );
};

export default DownloadsTable;